import React, {useState, useEffect, useContext, useRef} from "react";
import axios from "../../../common/axios";
import {
    Select,
    message,
    Row,
    Col,
    Space
} from "antd";
import {UserContext} from "../../../common/UserContext";
import {BrandContext} from "../../../common/BrandContext";
import TestResultBarGraph from "./TestResultBarGraph";
import CircularLoader from "../../../common/CircularLoader";
import TableAntD from "../../../common/TableAntD";
import {Download} from "@mui/icons-material/";
import Button, {BUTTON_TYPE} from "../../../common/Button";
import ResponsiveRow from "../../../common/ResponsiveRow";
import GenericDialog from "../../../common/GenericDialog";
import MathJaxHTMLContent from "../../../common/MathJaxHTMLContent";
import {ArrowLeftIcon} from "../../../common/icons";
import TestQuestion from "../../student/assessments/TestQuestion";

const { Option } = Select;

const TestAssignmentTestResults = (props) => {
    const {testAssignmentId, handleBack, handleViewTestResult} = props,
        {user} = useContext(UserContext),
        brandConfig = useContext(BrandContext),
        [isLoading, setLoading] = useState(false),
        [testAssignmentResults, setTestAssignmentResults] = useState(),
        [classId, setClassId] = useState(),
        [testResultOrder, setTestResultOrder] = useState(),
        [questionResultDialogOpen, setQuestionResultDialogOpen] = useState(false),
        [question, setQuestion] = useState(),
        [questionResult, setQuestionResult] = useState();

    const fetchTestAssignmentData = async () => {
        if(!testAssignmentId) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get('/web/ui/test/teacher/testAssignment/testResults', {
                params: {
                    testAssignmentId: testAssignmentId,
                    classId: classId,
                    testResultOrder: testResultOrder
                }
            });
            setTestAssignmentResults(response.data);
        } catch (error) {
            console.error(error);
            message.error('Failed to load test data');
        }
        setLoading(false);
    };

    useEffect(() => {
        if(testAssignmentId == undefined) {
            return
        }

        fetchTestAssignmentData();
    }, [classId, testResultOrder, testAssignmentId]);

    const handleTestReportClick = (reportFormat) => {
        if (!testAssignmentResults.testAssignmentId) {
            return;
        }

        const url = `/reports/loadTestResultReport.action?reportFormat=${reportFormat}&testAssignmentId=${testAssignmentResults.testAssignmentId}${
            classId ? `&classId=${classId}` : ''
        }`;
        window.open(url, '_blank')?.focus();
    };

    const handleReorder = (value) => {
        setTestResultOrder(value);
    };

    const handleClassChange = (value) => {
        setClassId(value);
    };

    const handleSubmitTestAssignment = (userId, testAssignmentId) => {
        if(!userId || !testAssignmentId) {
            return;
        }
        submitTestAssignmentForStudent(userId, testAssignmentId, classId);
    };

    const handleLoadStudentTestResults = (testResult) => {
        if(testResult == undefined) {
            return;
        }
        handleViewTestResult(testResult);
    };

    if(testAssignmentResults == undefined) {
        return <CircularLoader/>;
    }

    const {
        test,
        testResults,
        classes,
        testAverage,
        testAveragePercentage,
    } = testAssignmentResults;

    const handleOpenDialog = (locQuestion, locQuestionResult)=>{
        setQuestion(locQuestion);
        setQuestionResult(locQuestionResult);
        if(locQuestion && locQuestionResult) {
            setQuestionResultDialogOpen(true);
        }
    }

    const columns = [
        {
            title: 'Student',
            dataIndex: 'studentName',
            key: 'studentName',
            width: '10%',
            render: (_, record) => {
                return (
                    <a aria-label={'view '+record.firstName + record.lastName+' results'} tabIndex={0} onKeyDown={(e) => {if(e.key === 'Enter'){handleLoadStudentTestResults(record)}}} onClick={() => handleLoadStudentTestResults(record)}>{record.firstName} {record.lastName} (view results)</a>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'testAssignmentStatusDescription',
            key: 'testAssignmentStatusDescription',
            render: (_, record) => record.testAssignmentStatusDescription
        },

        {
            title: 'Results',
            dataIndex: 'testQuestionResults',
            key: 'testQuestionResults',
            width: '70%',
            render: (_, record) => <TestResultBarGraph handleOpenDialog={handleOpenDialog} testResult={record}/>
        },
        {
            title: 'Scores',
            dataIndex: 'score',
            key: 'score',
            align: 'center',
            render: (_, record) => {
                const canSubmit = record.total == undefined &&
                    (record.testAssignmentStatus == 'STARTED' ||
                    record.testAssignmentStatus == 'ABANDONED' ||
                    record.testAssignmentStatus == 'TIMED_OUT');

                if(canSubmit) {
                    return <>
                        {canSubmit && (
                            <Button onClick={() => handleSubmitTestAssignment(record.userId, record.testAssignmentId)}>
                                Submit
                            </Button>
                        )}
                    </>
                }
                if(record.mark == undefined || record.maxMark == undefined) {
                    return 'N/A';
                }
                return `${record.mark}/${record.maxMark}`;
            }
        },
        {
            title: 'Percent',
            dataIndex: 'percentCorrect',
            key: 'percentCorrect',
            width: '10%',
            align: 'center',
            render: (_, record) => {
                return record.percentCorrect != undefined ? record.percentCorrect + '%' : 'N/A'
            }
        }
    ];

    return (
        <div>
            <ResponsiveRow items={[<div className={'flexSpaceBetween'}><h2>{test.name}</h2>
                <Button type={BUTTON_TYPE.TEXT} onClick={handleBack}>
                    <ArrowLeftIcon/> Back to assigned {brandConfig.testsName}
                </Button></div>]}/>
            <ResponsiveRow items={[<>{test.assignedDate && <><strong>Assigned:</strong> {user.dateTimeMinuteFormat.format(test.assignedDate)}&nbsp;</>}
                <strong>Marking:</strong> {test.maxMark} from {test.total} question(s)</>]}>
            </ResponsiveRow>
            <TableAntD
                dynamicWidth={true}
                style={{ marginTop: 16 }}
                title={() => (
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space>
                                {testAverage != undefined && testAverage > 0 && <span>Average score: {testAverage} ({testAveragePercentage}%)</span>}
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                {test.multiClass && classes && (
                                    <>
                                        <span>Class:</span>
                                        <Select
                                            placeholder="Select Class"
                                            style={{ width: 160 }}
                                            onChange={handleClassChange}
                                            allowClear
                                            value={classId || undefined}
                                        >
                                            {(classes).map((c) => (
                                                <Option key={c.classId} value={c.classId}>
                                                    {c.className}
                                                </Option>
                                            ))}
                                        </Select>
                                    </>
                                )}
                                <Button ariaLabel={'download test results PDF'} onClick={() => handleTestReportClick("PDF")}>
                                    PDF<Download/>
                                </Button>
                                <Button ariaLabel={'download test results Spreadsheet'} onClick={() => handleTestReportClick("XLSX")}>
                                    Excel<Download/>
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                )}
                dataSource={testResults}
                rowKey="testResultId"
                columnData={columns}
                loading={isLoading}
                pagination={false}
                locale={{
                    emptyText: (
                        <div>
                            No {brandConfig.testName.toLowerCase()} results found.
                        </div>
                    )
                }}
            />
            <GenericDialog open={questionResultDialogOpen}
               title="Test question"
               onClose={() => {
                   setQuestionResultDialogOpen(false);
               }}
               buttons={[{name: 'Close'}]}>
                <TestQuestion questionResult={questionResult} testQuestion={question} showExamCitations={false} showUserAnswers={true} />
            </GenericDialog>
        </div>
    );
};

export default TestAssignmentTestResults;
